// One editing state machine, shared by every form in the app.
//
// The three things that are easy to get wrong, and are therefore done exactly
// once here:
//
//  1. OPTIMISTIC LOCKING. Every mutable row carries `version`; a PATCH sends it
//     as `If-Match` and a mismatch is 409 (§06). A 409 is NOT an error message —
//     it means somebody else changed this row while this form was open, and the
//     only two honest ways out are "keep mine, on top of theirs" and "take
//     theirs". So a conflict never discards the draft, never silently retries,
//     and always offers to SHOW what changed before the user chooses.
//
//  2. PARTIAL UPDATES. The `*Update` schemas are `.partial()` — and a partial
//     of a field carrying `.default(null)` still injects that default for keys
//     that were absent. Parsing `{ name }` against `PartnerUpdate` yields
//     `{ name, notes: null }`, which would blank a note the user never touched.
//     So the body sent is the parsed value NARROWED BACK to the keys that
//     actually changed, and a PATCH only ever mentions edited fields.
//
//  3. CLIENT-SIDE VALIDATION AGAINST THE CONTRACT. The same `@crm/shared`
//     schema the API validates against runs here first. The server stays the
//     authority — a reference that doesn't exist or a uniqueness clash can only
//     be known there — but nobody should need a round trip to learn a required
//     field is empty.

import { useCallback, useMemo, useState } from "react";
import type { z } from "zod";
import type { FieldError } from "../api/problem.js";
import { useIdempotencyKey } from "../api/idempotency.js";
import { isApiProblem, type ApiProblem } from "../api/problem.js";
import { serverFieldErrors, zodFieldErrors, type FieldErrors } from "./formErrors.js";

/**
 * One value of a write body.
 *
 * Almost every field of this contract is a scalar. A term line's selector, its
 * quantity rule and its milestone plan are OBJECTS, and they belong in the body
 * like any other field: a form that carried them outside the wire shape would
 * validate one line and send another, and the diff below would never see an
 * edit that touched only one of them. So an object is a wire value too, and
 * [sameValue] compares it structurally.
 */
export type WireValue = string | number | boolean | null | undefined | object;
export type Wire = Readonly<Record<string, WireValue>>;

export interface VersionedRecord {
  id: string;
  version: number;
}

export type EditorMode<TRecord extends VersionedRecord> =
  | { kind: "create" }
  | { kind: "update"; record: TRecord };

export interface FieldDifference {
  field: string;
  mine: WireValue;
  theirs: WireValue;
  /** False means they changed a field this form did not touch — context, not a clash. */
  editedByYou: boolean;
}

export type ConflictState<TRecord> =
  | { phase: "detected"; problem: ApiProblem }
  | { phase: "reloading" }
  | { phase: "reload_failed"; error: unknown }
  | { phase: "compared"; current: TRecord; differences: readonly FieldDifference[] };

export interface EditorConfig<
  TDraft extends object,
  TCreate,
  TUpdate,
  TRecord extends VersionedRecord,
> {
  mode: EditorMode<TRecord>;
  /** The empty form. Used for `create`, and as the shape of a draft. */
  blankDraft: TDraft;
  draftFromRecord: (record: TRecord) => TDraft;
  /** Draft (what the inputs hold) -> wire body (what the contract describes). */
  toWire: (draft: TDraft) => Wire;
  /**
   * Omitted by a form that has no create path — a deal fact is one, since a
   * fact is ingested as a batch and never posted as a record. Absent rather
   * than stubbed, so the type says so.
   */
  createSchema?: z.ZodType<TCreate>;
  updateSchema: z.ZodType<TUpdate>;
  /**
   * Rules a wire object can't express, because they are about the FORM: "this
   * selector is not JSON the evaluator reads" is a better message than the type
   * error a half-typed expression would otherwise produce.
   */
  validateDraft?: (draft: TDraft) => FieldErrors;
  /**
   * The wire field a draft field is written to, where the two names differ.
   *
   * Every message is keyed by the field the CONTRACT names — that is what the
   * server sends and what the schema reports — while an input holds the draft
   * field. A form whose rate input is `rate` and whose body says `rate_minor`
   * would otherwise clear a message nobody is looking at, and leave "this line
   * needs a rate" under an input that now holds one. Omitted where the two
   * names agree, which is almost everywhere.
   */
  wireField?: (draftField: keyof TDraft) => string;
  /**
   * Fields the contract will only accept together.
   *
   * Nothing needs this today: the cross-field rules that survive are about a
   * shape the whole body states at once, and the term-line form validates them
   * with `termLineShapeProblem` before the request goes out. It stays because a
   * diff-based PATCH is what makes the case possible at all — a schema that
   * refuses one of a pair without the other would otherwise be handed a body
   * this editor built and the schema itself rejects.
   */
  coupledFields?: readonly (readonly string[])[];
  create?: (body: TCreate, idempotencyKey: string) => Promise<TRecord>;
  update: (id: string, body: TUpdate, version: number) => Promise<TRecord>;
  fetchCurrent: (id: string) => Promise<TRecord>;
  onSaved: (record: TRecord) => void;
}

export interface Editor<TDraft extends object, TRecord> {
  draft: TDraft;
  setField: <K extends keyof TDraft>(field: K, value: TDraft[K]) => void;
  errors: FieldErrors;
  /** A failure that isn't about one field: forbidden, reference_not_found, offline. */
  failure: unknown;
  /** Server field errors naming something this form doesn't render. */
  unmappedErrors: readonly FieldError[];
  conflict: ConflictState<TRecord> | null;
  isSaving: boolean;
  submit: () => void;
  /** Conflict: fetch what is stored now and show it beside the draft. */
  reloadCurrent: () => void;
  /** Conflict: apply the draft on top of the reloaded version. */
  keepMine: () => void;
  /** Conflict: replace the draft with the stored values. Only ever explicit. */
  useTheirs: () => void;
}

/** A plain object, for the structural half of [sameValue]. */
function isWireRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isWireList(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}

/**
 * Whether the draft's value and the stored one are the same answer.
 *
 * Structural for objects and lists. An object clause comes back from the API
 * as one object and is read from the form's text as another, so two equal
 * expressions are never the same reference: `Object.is` alone would report
 * every unchanged clause as an edit, and the body would restate a rule nobody
 * touched.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (isWireList(a) && isWireList(b)) {
    return a.length === b.length && a.every((item, index) => sameValue(item, b[index]));
  }
  if (isWireRecord(a) && isWireRecord(b)) {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => key in b && sameValue(a[key], b[key]));
  }
  return false;
}

/**
 * `parsed` narrowed to the keys that were actually edited — see note 2 above.
 * The VALUES come from the parsed object (trimmed, coerced), the KEYS from the
 * diff.
 */
function onlyEditedKeys<T>(parsed: T, editedKeys: readonly string[]): T {
  const source = parsed as Record<string, unknown>;
  const narrowed: Record<string, unknown> = {};
  for (const key of editedKeys) {
    if (key in source) narrowed[key] = source[key];
  }
  return narrowed as T;
}

export function useEditor<TDraft extends object, TCreate, TUpdate, TRecord extends VersionedRecord>(
  config: EditorConfig<TDraft, TCreate, TUpdate, TRecord>,
): Editor<TDraft, TRecord> {
  const {
    mode,
    blankDraft,
    draftFromRecord,
    toWire,
    createSchema,
    updateSchema,
    validateDraft,
    wireField,
    coupledFields,
    create,
    update,
    fetchCurrent,
    onSaved,
  } = config;

  const [draft, setDraft] = useState<TDraft>(() =>
    mode.kind === "create" ? blankDraft : draftFromRecord(mode.record),
  );
  /**
   * The record this form LOADED — the version `If-Match` carries. It is state,
   * not a prop read on every render, because a background refetch must not
   * quietly re-point the form at a newer version: that would turn a real
   * conflict into a silent overwrite, which is the exact failure §06's version
   * column exists to prevent. It moves only when the user reloads after a 409.
   */
  const [baseline, setBaseline] = useState<TRecord | null>(
    mode.kind === "create" ? null : mode.record,
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [unmappedErrors, setUnmappedErrors] = useState<readonly FieldError[]>([]);
  const [failure, setFailure] = useState<unknown>(null);
  const [conflict, setConflict] = useState<ConflictState<TRecord> | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { keyFor } = useIdempotencyKey();

  const setField = useCallback(
    <K extends keyof TDraft>(field: K, value: TDraft[K]): void => {
      setDraft((previous) => ({ ...previous, [field]: value }));
      // Clearing the message the moment the field is touched: leaving
      // "required" under a field that now has a value reads as though the form
      // is still refusing it. The message is keyed by the field the CONTRACT
      // names, which is not always the name the draft uses.
      const messageKey = wireField === undefined ? String(field) : wireField(field);
      setErrors((previous) => {
        if (!(messageKey in previous)) return previous;
        const { [messageKey]: _cleared, ...rest } = previous;
        return rest;
      });
    },
    [wireField],
  );

  const knownFields = useMemo(() => Object.keys(toWire(blankDraft)), [toWire, blankDraft]);

  const handleFailure = useCallback(
    (error: unknown): void => {
      if (isApiProblem(error) && error.code === "crm-conflict") {
        setConflict({ phase: "detected", problem: error });
        return;
      }
      if (isApiProblem(error) && error.code === "crm-validation-failed") {
        const mapped = serverFieldErrors(error, knownFields);
        setErrors(mapped.fields);
        setUnmappedErrors(mapped.unmapped);
        // Every error landed on a field: the fields say it, the bar would only
        // repeat it. Nothing mapped: the bar is the only thing that can.
        setFailure(Object.keys(mapped.fields).length === 0 ? error : null);
        return;
      }
      setFailure(error);
    },
    [knownFields],
  );

  const runSubmit = useCallback(
    async (against: TRecord | null): Promise<void> => {
      setErrors({});
      setUnmappedErrors([]);
      setFailure(null);
      setConflict(null);

      const draftErrors = validateDraft?.(draft) ?? {};
      if (Object.keys(draftErrors).length > 0) {
        setErrors(draftErrors);
        return;
      }

      const wire = toWire(draft);
      setIsSaving(true);
      try {
        if (against === null) {
          if (createSchema === undefined || create === undefined) {
            throw new Error("This editor has no create path — it can only update a record");
          }
          const parsed = createSchema.safeParse(wire);
          if (!parsed.success) {
            setErrors(zodFieldErrors(parsed.error));
            return;
          }
          onSaved(await create(parsed.data, keyFor(parsed.data)));
          return;
        }

        const record = against as unknown as Record<string, unknown>;
        const changed = new Set(
          Object.keys(wire).filter(
            (key) => wire[key] !== undefined && !sameValue(wire[key], record[key]),
          ),
        );
        for (const group of coupledFields ?? []) {
          if (group.some((field) => changed.has(field))) {
            for (const field of group) {
              if (wire[field] !== undefined) changed.add(field);
            }
          }
        }

        const editedKeys = [...changed];
        const edited: Record<string, WireValue> = {};
        for (const key of editedKeys) edited[key] = wire[key];

        const parsed = updateSchema.safeParse(edited);
        if (!parsed.success) {
          setErrors(zodFieldErrors(parsed.error));
          return;
        }

        const body = onlyEditedKeys(parsed.data, editedKeys);
        if (Object.keys(body as Record<string, unknown>).length === 0) {
          // Nothing to send. A PATCH here would burn a version number and could
          // fail with a 409 over a change the user did not make.
          onSaved(against);
          return;
        }

        onSaved(await update(against.id, body, against.version));
      } catch (error) {
        handleFailure(error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      draft,
      validateDraft,
      coupledFields,
      toWire,
      createSchema,
      updateSchema,
      create,
      update,
      keyFor,
      onSaved,
      handleFailure,
    ],
  );

  const submit = useCallback((): void => {
    void runSubmit(baseline);
  }, [runSubmit, baseline]);

  const reloadCurrent = useCallback((): void => {
    if (baseline === null) return;
    setConflict({ phase: "reloading" });
    void (async () => {
      try {
        const current = await fetchCurrent(baseline.id);
        const wire = toWire(draft);
        const loaded = baseline as unknown as Record<string, unknown>;
        const stored = current as unknown as Record<string, unknown>;

        const differences: FieldDifference[] = [];
        for (const [field, mine] of Object.entries(wire)) {
          if (mine === undefined) continue;
          const theirs = stored[field];
          if (sameValue(mine, theirs)) continue;
          differences.push({
            field,
            mine,
            theirs: theirs as WireValue,
            editedByYou: !sameValue(mine, loaded[field]),
          });
        }

        // From here on, "the version this form is working against" is the one
        // just read — so keeping the draft PATCHes on top of their change
        // rather than failing the same way a second time.
        setBaseline(current);
        setConflict({ phase: "compared", current, differences });
      } catch (error) {
        setConflict({ phase: "reload_failed", error });
      }
    })();
  }, [baseline, draft, fetchCurrent, toWire]);

  const keepMine = useCallback((): void => {
    if (conflict?.phase !== "compared") return;
    void runSubmit(conflict.current);
  }, [conflict, runSubmit]);

  const useTheirs = useCallback((): void => {
    if (conflict?.phase !== "compared") return;
    setDraft(draftFromRecord(conflict.current));
    setErrors({});
    setConflict(null);
  }, [conflict, draftFromRecord]);

  return {
    draft,
    setField,
    errors,
    failure,
    unmappedErrors,
    conflict,
    isSaving,
    submit,
    reloadCurrent,
    keepMine,
    useTheirs,
  };
}
